import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { FaCreditCard, FaCheckCircle, FaLock } from "react-icons/fa";
import {
  stripeMaintenancePriceIds,
  stripeMaintenancePriceIdsPrepay3Year,
  stripePriceIds,
  stripePriceIdsPrepay3Year,
  stripeSupportPriceIds,
  stripeSupportPriceIdsPrepay3Year,
} from "../config/stripePriceIds";
import {
  formatUsdWhole,
  maintenancePrices,
  planPrices,
  prepayTotalFromSelections,
  supportPrices,
  threeYearPrepayFromMonthly,
} from "../config/stripePlanPricing";
import { API_ENDPOINTS } from "../config/api";

const planNames: Record<string, string> = {
  small: "Small Plan (4–50 beds)",
  medium: "Medium Plan (51–80 beds)",
  "large-medium": "Large-Medium Plan (81–125 beds)",
  large: "Large Plan (126+ beds)",
};

const planFacilitySizes: Record<string, string> = {
  small: "4–50 beds",
  medium: "51–80 beds",
  "large-medium": "81–125 beds",
  large: "126–200 beds",
  enterprise: "201+ beds",
};

// Stripe configuration (API)
const STRIPE_CHECKOUT_ENDPOINT = API_ENDPOINTS.stripeCheckout;

const Payment: React.FC = () => {
  const [searchParams] = useSearchParams();

  const plan = searchParams.get("plan") || "small";
  const maintenance = searchParams.get("maintenance") === "true";
  const support = searchParams.get("support") === "true";
  const billingPrepay = searchParams.get("billing") === "prepay";

  // Personal & Facility Info
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [jobTitle, setJobTitle] = useState("");
  const [telephone, setTelephone] = useState("");
  const [email, setEmail] = useState("");
  const facilitySize = planFacilitySizes[plan] || planFacilitySizes.small;
  const [address, setAddress] = useState("");

  // Card & Billing Info
  const [cardNumber, setCardNumber] = useState("");
  const [cardExpiry, setCardExpiry] = useState("");
  const [cardCVC, setCardCVC] = useState("");
  const [cardName, setCardName] = useState("");
  const [billingAddress, setBillingAddress] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [zipCode, setZipCode] = useState("");

  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const base = planPrices[plan] ?? 250;
  const maintenanceFee = maintenance ? maintenancePrices[plan] ?? 0 : 0;
  const supportFee = support ? supportPrices[plan] ?? 0 : 0;
  const monthlyTotal = base + maintenanceFee + supportFee;
  const prepayThreeYearTotal = prepayTotalFromSelections({
    planKey: plan,
    includeMaintenance: maintenance,
    includeSupport: support,
  });

  const paymentPageQuery = React.useMemo(() => {
    const p = new URLSearchParams();
    p.set("plan", plan);
    if (maintenance) p.set("maintenance", "true");
    if (support) p.set("support", "true");
    if (billingPrepay) p.set("billing", "prepay");
    return p.toString();
  }, [plan, maintenance, support, billingPrepay]);

  const successPageQuery = React.useMemo(() => {
    const p = new URLSearchParams();
    p.set("success", "true");
    p.set("plan", plan);
    p.set("monthlyTotal", String(monthlyTotal));
    if (maintenance) p.set("maintenance", "true");
    if (support) p.set("support", "true");
    if (billingPrepay) {
      p.set("billing", "prepay");
      p.set("prepayTotal", String(prepayThreeYearTotal));
    }
    return p.toString();
  }, [
    plan,
    maintenance,
    support,
    billingPrepay,
    prepayThreeYearTotal,
    monthlyTotal,
  ]);

  const formatCardNumber = (value: string) => {
    const cleaned = value.replace(/\s/g, "");
    const formatted = cleaned.match(/.{1,4}/g)?.join(" ") || cleaned;
    return formatted.slice(0, 19);
  };

  const formatExpiry = (value: string) => {
    const cleaned = value.replace(/\D/g, "");
    if (cleaned.length >= 2) {
      return `${cleaned.slice(0, 2)}/${cleaned.slice(2, 4)}`;
    }
    return cleaned;
  };

  const handleCardNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatCardNumber(e.target.value);
    setCardNumber(formatted);
  };

  const handleExpiryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatExpiry(e.target.value);
    setCardExpiry(formatted);
  };

  const handleCVCChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, "").slice(0, 4);
    setCardCVC(value);
  };

  const redirectToStripeCheckout = async () => {
    const basePriceId = billingPrepay
      ? stripePriceIdsPrepay3Year[plan]
      : stripePriceIds[plan];

    if (!basePriceId) {
      console.error("No Stripe price ID configured for plan:", plan, {
        billingPrepay,
      });
      setError("Stripe price configuration is missing for this plan.");
      return;
    }

    // Build dynamic price IDs based on selected options
    const priceIds: string[] = [basePriceId];

    if (maintenance) {
      const maintenanceId = billingPrepay
        ? stripeMaintenancePriceIdsPrepay3Year[plan]
        : stripeMaintenancePriceIds[plan];
      if (maintenanceId) {
        priceIds.push(maintenanceId);
      }
    }

    if (support) {
      const supportId = billingPrepay
        ? stripeSupportPriceIdsPrepay3Year[plan]
        : stripeSupportPriceIds[plan];
      if (supportId) {
        priceIds.push(supportId);
      }
    }

    const origin = window.location.origin;
    const successUrl = `${origin}/payment-success?${successPageQuery}`;
    const cancelUrl = `${origin}/payment?${paymentPageQuery}&canceled=true`;

    const checkoutBody: Record<string, unknown> = {
      successUrl,
      cancelUrl,
      priceIds,
      customerEmail: email || undefined,
    };
    if (billingPrepay) {
      checkoutBody.billingMode = "prepay";
      checkoutBody.prepayTermMonths = 36;
      checkoutBody.prepayDiscountPercent = 10;
      checkoutBody.prepayAmountUsd = prepayThreeYearTotal;
      checkoutBody.monthlyAmountUsd = monthlyTotal;
    }

    try {
      const response = await fetch(STRIPE_CHECKOUT_ENDPOINT, {
        method: "POST",
        headers: {
          Accept: "*/*",
          "Content-Type": "application/json",
        },
        body: JSON.stringify(checkoutBody),
      });

      if (!response.ok) {
        throw new Error("Failed to create Stripe checkout session.");
      }

      const data = await response.json();

      if (!data?.checkoutUrl) {
        throw new Error("Checkout URL missing in response.");
      }

      window.location.href = data.checkoutUrl;
    } catch (err) {
      console.error(err);
      setError("Unable to start Stripe Checkout. Please try again.");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (
      !firstName ||
      !lastName ||
      !jobTitle ||
      !telephone ||
      !email ||
      !facilitySize ||
      !address ||
      !city ||
      !state ||
      !zipCode
    ) {
      setError("Please fill in all required personal details.");
      return;
    }

    setLoading(true);

    try {
      // --- API REQUEST ---
      const response = await fetch(
        API_ENDPOINTS.saveOrganizationDraft,
        {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            organizationDraftId: 0,
            firstName,
            lastName,
            title: jobTitle,
            email,
            telephoneNumber: telephone,
            facilitySize,
            address,
          }),
        }
      );

      const rawResult = await response.text();
      let apiResult: any = null;

      try {
        apiResult = JSON.parse(rawResult);
      } catch (parseError) {
        console.warn("Unable to parse draft response as JSON:", parseError);
      }

      if (!response.ok) {
        const backendMsg =
          apiResult?.message ||
          apiResult?.error ||
          apiResult?.errors?.join(", ") ||
          rawResult ||
          "Failed to save organization draft.";
        throw new Error(backendMsg);
      }

      if (!apiResult?.succeeded) {
        const backendMsg =
          apiResult?.message ||
          apiResult?.error ||
          apiResult?.errors?.join(", ") ||
          "Organization draft did not succeed.";
        throw new Error(backendMsg);
      }

      // After saving organization draft successfully, go to Stripe Checkout
      await redirectToStripeCheckout();
    } catch (err: any) {
      console.error(err);
      setError(err?.message || "Something went wrong while saving your information.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.section
      initial={{ opacity: 0, y: 40 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.7, type: "spring" }}
      className="max-w-2xl mx-auto py-12 px-4"
    >
      {/* PLAN SUMMARY */}
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 0.1, type: "spring" }}
        className="bg-gradient-to-r from-blue-100 to-blue-50 rounded-3xl shadow-lg p-8 mb-8"
      >
        <h2 className="text-3xl font-extrabold text-blue-800 mb-4 text-center">
          Complete Your Purchase
        </h2>
        <div className="bg-white rounded-lg p-6 space-y-3">
          <div className="flex justify-between items-center">
            <span className="text-gray-700 font-semibold">Plan:</span>
            <span className="text-blue-800 font-bold">{planNames[plan]}</span>
          </div>
          <div className="flex justify-between items-center gap-2">
            <span className="text-gray-700">Billing:</span>
            <span className="text-gray-800 font-semibold text-right">
              {billingPrepay
                ? "3-year prepay (10% savings)"
                : "Monthly"}
            </span>
          </div>
          <div className="flex justify-between items-center gap-2">
            <span className="text-gray-700">
              {billingPrepay
                ? "Base plan (3-year prepay):"
                : "Base monthly fee:"}
            </span>
            <span className="text-gray-800 font-semibold text-right">
              {billingPrepay
                ? formatUsdWhole(threeYearPrepayFromMonthly(base))
                : `$${base}/mo`}
            </span>
          </div>
          {maintenance && (
            <div className="flex justify-between items-center gap-2">
              <span className="text-gray-700">
                {billingPrepay
                  ? "Maintenance (3-year prepay add-on):"
                  : "Maintenance module:"}
              </span>
              <span className="text-gray-800 font-semibold text-right">
                {billingPrepay
                  ? `+${formatUsdWhole(
                      threeYearPrepayFromMonthly(maintenancePrices[plan] ?? 0)
                    )}`
                  : `+$${maintenancePrices[plan]}/mo`}
              </span>
            </div>
          )}
          {support && (
            <div className="flex justify-between items-center gap-2">
              <span className="text-gray-700">
                {billingPrepay
                  ? "Support (3-year prepay add-on):"
                  : "Support package:"}
              </span>
              <span className="text-gray-800 font-semibold text-right">
                {billingPrepay
                  ? `+${formatUsdWhole(
                      threeYearPrepayFromMonthly(supportPrices[plan] ?? 0)
                    )}`
                  : `+$${supportPrices[plan]}/mo`}
              </span>
            </div>
          )}
          <div className="border-t pt-3 mt-3">
            <div className="flex justify-between items-center">
              <span className="text-lg font-bold text-blue-800">
                {billingPrepay
                  ? "3-year prepay total (10% off):"
                  : "Total per month:"}
              </span>
              <span className="text-2xl font-extrabold text-blue-700 text-right">
                {billingPrepay
                  ? formatUsdWhole(prepayThreeYearTotal)
                  : `$${monthlyTotal}/mo`}
              </span>
            </div>
            {billingPrepay && (
              <p className="text-xs text-gray-500 mt-2 text-right leading-snug">
                Equivalent to {formatUsdWhole(monthlyTotal)}/mo before discount;
                one-time checkout for 36 months.
              </p>
            )}
          </div>
        </div>
      </motion.div>

      {/* FORM SECTION */}
      <form
          onSubmit={handleSubmit}
          className="bg-white rounded-2xl shadow-lg p-8 space-y-6"
        >
        <div className="flex items-center gap-2 text-blue-700 mb-4">
          <FaLock />
          <span className="text-sm font-semibold">
            Secure payment processing. Your information is encrypted.
          </span>
        </div>

        {/* PERSONAL DETAILS */}
        <h3 className="text-lg font-bold text-blue-800">Personal Details</h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-blue-800 font-semibold mb-2">
              First Name *
            </label>
            <input
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              className="w-full border rounded px-4 py-2 focus:ring-2 focus:ring-blue-200 focus:outline-none"
              placeholder="John"
              required
            />
          </div>
          <div>
            <label className="block text-blue-800 font-semibold mb-2">
              Last Name *
            </label>
            <input
              type="text"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              className="w-full border rounded px-4 py-2 focus:ring-2 focus:ring-blue-200 focus:outline-none"
              placeholder="Doe"
              required
            />
          </div>
        </div>

        <div>
          <label className="block text-blue-800 font-semibold mb-2">
            Title *
          </label>
          <input
            type="text"
            value={jobTitle}
            onChange={(e) => setJobTitle(e.target.value)}
            className="w-full border rounded px-4 py-2 focus:ring-2 focus:ring-blue-200 focus:outline-none"
            placeholder="Administrator"
            required
          />
        </div>

        <div>
          <label className="block text-blue-800 font-semibold mb-2">
            Email *
          </label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full border rounded px-4 py-2 focus:ring-2 focus:ring-blue-200 focus:outline-none"
            placeholder="example@domain.com"
            required
          />
        </div>

        <div>
          <label className="block text-blue-800 font-semibold mb-2">
            Telephone Number *
          </label>
          <input
            type="tel"
            value={telephone}
            onChange={(e) =>
              setTelephone(e.target.value.replace(/\D/g, "").slice(0, 15))
            }
            className="w-full border rounded px-4 py-2 focus:ring-2 focus:ring-blue-200 focus:outline-none"
            placeholder="1234567890"
            required
          />
        </div>

        <div>
          <label className="block text-blue-800 font-semibold mb-2">
            Facility Size *
          </label>
          <input
            type="text"
            value={facilitySize}
            readOnly
            className="w-full border rounded px-4 py-2 bg-gray-100 text-gray-700 cursor-not-allowed focus:outline-none"
            required
          />
        </div>

        <div>
          <label className="block text-blue-800 font-semibold mb-2">
            Address *
          </label>
          <input
            type="text"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            className="w-full border rounded px-4 py-2 focus:ring-2 focus:ring-blue-200 focus:outline-none"
            placeholder="123 Main Street"
            required
          />
        </div>

        <div className="grid grid-cols-3 gap-4">
          <div className="col-span-2">
            <label className="block text-blue-800 font-semibold mb-2">
              City *
            </label>
            <input
              type="text"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              className="w-full border rounded px-4 py-2 focus:ring-2 focus:ring-blue-200 focus:outline-none"
              placeholder="City"
              required
            />
          </div>
          <div>
            <label className="block text-blue-800 font-semibold mb-2">
              State *
            </label>
            <input
              type="text"
              value={state}
              onChange={(e) => setState(e.target.value)}
              className="w-full border rounded px-4 py-2 focus:ring-2 focus:ring-blue-200 focus:outline-none"
              placeholder="State"
              maxLength={2}
              required
            />
          </div>
        </div>

        <div>
          <label className="block text-blue-800 font-semibold mb-2">
            ZIP Code *
          </label>
          <input
            type="text"
            value={zipCode}
            onChange={(e) =>
              setZipCode(e.target.value.replace(/\D/g, "").slice(0, 10))
            }
            className="w-full border rounded px-4 py-2 focus:ring-2 focus:ring-blue-200 focus:outline-none"
            placeholder="12345"
            maxLength={10}
            required
          />
        </div>

        {/* CARD DETAILS */}
        {/* <h3 className="text-lg font-bold text-blue-800 mt-6">
          Payment Details
        </h3>

        <div>
          <label className="block text-blue-800 font-semibold mb-2">
            Cardholder Name *
          </label>
          <input
            type="text"
            value={cardName}
            onChange={(e) => setCardName(e.target.value)}
            className="w-full border rounded px-4 py-2 focus:ring-2 focus:ring-blue-200 focus:outline-none"
            placeholder="John Doe"
            required
          />
        </div>

        <div>
          <label className="block text-blue-800 font-semibold mb-2">
            Card Number *
          </label>
          <div className="relative">
            <FaCreditCard className="absolute left-3 top-3 text-blue-300" />
            <input
              type="text"
              value={cardNumber}
              onChange={handleCardNumberChange}
              className="w-full border rounded pl-10 pr-4 py-2 focus:ring-2 focus:ring-blue-200 focus:outline-none"
              placeholder="1234 5678 9012 3456"
              maxLength={19}
              required
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-blue-800 font-semibold mb-2">
              Expiry Date *
            </label>
            <input
              type="text"
              value={cardExpiry}
              onChange={handleExpiryChange}
              className="w-full border rounded px-4 py-2 focus:ring-2 focus:ring-blue-200 focus:outline-none"
              placeholder="MM/YY"
              maxLength={5}
              required
            />
          </div>
          <div>
            <label className="block text-blue-800 font-semibold mb-2">
              CVC *
            </label>
            <input
              type="text"
              value={cardCVC}
              onChange={handleCVCChange}
              className="w-full border rounded px-4 py-2 focus:ring-2 focus:ring-blue-200 focus:outline-none"
              placeholder="123"
              maxLength={4}
              required
            />
          </div>
        </div> */}

        {/* BILLING ADDRESS */}
        {/* <div className="border-t pt-6 mt-6">
          <h3 className="text-lg font-bold text-blue-800 mb-4">
            Billing Address
          </h3>
          <div className="space-y-4">
            <div>
              <label className="block text-blue-800 font-semibold mb-2">
                Street Address *
              </label>
              <input
                type="text"
                value={billingAddress}
                onChange={(e) => setBillingAddress(e.target.value)}
                className="w-full border rounded px-4 py-2 focus:ring-2 focus:ring-blue-200 focus:outline-none"
                placeholder="123 Main Street"
                required
              />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="col-span-2">
                <label className="block text-blue-800 font-semibold mb-2">
                  City *
                </label>
                <input
                  type="text"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  className="w-full border rounded px-4 py-2 focus:ring-2 focus:ring-blue-200 focus:outline-none"
                  placeholder="City"
                  required
                />
              </div>
              <div>
                <label className="block text-blue-800 font-semibold mb-2">
                  State *
                </label>
                <input
                  type="text"
                  value={state}
                  onChange={(e) => setState(e.target.value)}
                  className="w-full border rounded px-4 py-2 focus:ring-2 focus:ring-blue-200 focus:outline-none"
                  placeholder="State"
                  maxLength={2}
                  required
                />
              </div>
            </div>
            <div>
              <label className="block text-blue-800 font-semibold mb-2">
                ZIP Code *
              </label>
              <input
                type="text"
                value={zipCode}
                onChange={(e) =>
                  setZipCode(e.target.value.replace(/\D/g, "").slice(0, 10))
                }
                className="w-full border rounded px-4 py-2 focus:ring-2 focus:ring-blue-200 focus:outline-none"
                placeholder="12345"
                maxLength={10}
                required
              />
            </div>
          </div>
        </div> */}

        {/* ERROR MESSAGE */}
        {error && (
          <div className="relative bg-red-50 border border-red-200 text-red-700 px-4 py-3 pr-10 rounded">
            {error}
            <button
              type="button"
              onClick={() => setError("")}
              className="absolute top-2 right-2 w-7 h-7 flex items-center justify-center rounded hover:bg-black/10 text-lg leading-none"
              aria-label="Close error"
            >
              ×
            </button>
          </div>
        )}

        {/* BUTTON */}
        <motion.button
          whileTap={{ scale: 0.97 }}
          whileHover={{ scale: 1.02 }}
          type="submit"
          disabled={loading}
          className={`w-full bg-gradient-to-r from-blue-600 to-blue-500 text-white px-6 py-4 rounded-lg shadow-lg font-bold text-lg flex items-center justify-center gap-2 mt-4 ${
            loading
              ? "opacity-70 cursor-not-allowed"
              : "hover:from-blue-700 hover:to-blue-600"
          }`}
        >
          {loading ? (
            "Processing..."
          ) : (
            <>
              Complete Purchase <FaCheckCircle />
            </>
          )}
        </motion.button>

          <p className="text-xs text-gray-500 text-center mt-4">
            By completing this purchase, you agree to our Terms of Service and
            Privacy Policy.
          </p>
        </form>
    </motion.section>
  );
};

export default Payment;
